import React, { useState, useEffect, useCallback } from "react";
import { format, parse, parseISO } from "date-fns";
import { useAuth } from "@/hooks/AuthContext";
import { useProjects } from "@/hooks/Projects";
import api from "@/api/axios";
import {API, ym} from "@/App";

// --- UI Components ---
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Calendar,
  Layers,
  FileText,
  CircleDollarSign,
  CheckCircle2,
  Hourglass,
  XCircle, User2,
} from "lucide-react";
import { VoucherForm } from "@/components/ui/VoucherForm";
import AttendanceComponent from "@/components/AttendanceComponent";

// --- The calendar is now just a visual aid ---
import QuickTimeCalendar from "@/components/calendar/QuickTImeCalender";

function MyDayPage() {
  const { user } = useAuth();
  const projects = useProjects();
  const userId = user?.id || "";
  const [month, setMonth] = useState(ym(new Date()));
  const [userVouchers, setUserVouchers] = useState([]);
  // --- State for page components ---
  const [attendance, setAttendance] = useState([]);
  const [rec, setRec] = useState(null);
  const [ts, setTs] = useState(null);
  const [monthlyTimeData, setMonthlyTimeData] = useState(null);
  const [loadingCalendar, setLoadingCalendar] = useState(true);
  const [calendarError, setCalendarError] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [tasks, setTasks] = useState([]);

  const fetchUserTasks = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await api.get(`${API}/tasks`, { params: { assignee_id: userId } });
      console.log("Fetched tasks:", res.data);
      setTasks(res.data || []);
    } catch (err) {
      console.error("Failed to fetch tasks:", err);
    }
  }, [userId]);


  useEffect(() => {
    fetchUserTasks();
  }, [fetchUserTasks]);

  // --- State for the Quick Time Entry form ---
  const [form, setForm] = useState({
    date: format(new Date(), "yyyy-MM-dd"), // Format: "2023-10-27"
    project_id: "",
    task_id: "",
    start_at: "09:30", // Format: "HH:mm"
    end_at: "10:30",   // Format: "HH:mm"
    break_minutes: 0,
    billable: true,
    location: "office",
    notes: "",
  });


  // attendance + reconcile only depend on userId
  const fetchDailyData = async () => {
    try {
      const [attendanceRes, reconcileRes] = await Promise.all([
        api.get(`${API}/attendance/today`, { params: { user_id: userId } }),
        api.get(`${API}/time-entries/reconcile/today`, { params: { user_id: userId } })
      ]);
      setAttendance(attendanceRes.data || []);
      setRec(reconcileRes.data);
    } catch (err) {
      console.error("Error fetching daily data:", err);
      setCalendarError("Failed to fetch daily data");
    }
  };
  // monthly timesheet depends on userId + selected month
  const fetchMonthlyData = async () => {
    try {
      setLoadingCalendar(true)
      const currentMonth = format(new Date(form.date), "yyyy-MM");
      const monthlyRes = await api.get(`${API}/time-entries/daily`, {
        params: { month: currentMonth, user_id: userId }
      });
      setMonthlyTimeData(monthlyRes.data);
      setLoadingCalendar(false)
    } catch (err) {
      console.error("Error fetching monthly data:", err);
      setCalendarError("Failed to fetch monthly data");
    }
  };

  useEffect(() => {
    if (!userId) return;



    fetchDailyData();
  }, [userId]);

  useEffect(() => {
    if (!userId) return;

    // weekly timesheet only depends on userId
    const fetchWeeklyData = async () => {
      try {
        const weekRes = await api.get(`${API}/time-entries/week`, {
          params: { user_id: userId }
        });
        setTs(weekRes.data);
      } catch (err) {
        console.error("Error fetching weekly data:", err);
        setCalendarError("Failed to fetch weekly data");
      }
    };

    fetchWeeklyData();
  }, [userId]);

  useEffect(() => {
    if (!userId || !form.date) return;



    fetchMonthlyData();
  }, [userId, form.date]);
  const fetchUserVouchers = async () => {
    if (!userId) return;
    try {
      const res = await api.get(`${API}/vouchers?month=${month}`);
      // Sort by date descending and pick the most recent
      const sorted = res.data.vouchers.sort((a, b) => new Date(b.date) - new Date(a.date));
      setUserVouchers(sorted);
    } catch (err) {
      console.error("Failed to fetch user vouchers:", err);
    }
  };

  const handleVoucher = async (voucherID, status) => {
    try {
      const res = await api.post(`${API}/vouchers/${voucherID}`,  { status });

      fetchUserVouchers()
    } catch (err) {
      console.error("Failed to fetch user vouchers:", err);
    }
  }

  useEffect(() => {
    fetchUserVouchers();
  }, [userId, month]);




  // --- Core Validation Logic ---
  /**
   * Validates a given time slot against the fetched monthly data.
   * This is the single source of truth for submission rules.
   * @param {string} startTimeStr - "HH:mm"
   * @param {string} endTimeStr - "HH:mm"
   * @param {string} dateStr - "YYYY-MM-DD"
   * @returns {{isValid: boolean, message: string}}
   */
  const isTimeSlotValid = (startTimeStr, endTimeStr, dateStr) => {
    if (!monthlyTimeData || !monthlyTimeData.data) {
      return { isValid: false, message: "Validation data not loaded. Please wait." };
    }

    // Find the data for the specific day from the form
    const apiDateStr = format(parseISO(dateStr), 'dd-MMM-yyyy');
    let dayData = null;
    for (const week of monthlyTimeData.data) {
      const foundDay = week.days.find(d => d.date === apiDateStr);
      if (foundDay) {
        dayData = foundDay;
        break;
      }
    }

    if (!dayData) {
      return { isValid: false, message: `No attendance or timesheet data found for ${dateStr}.` };
    }

    // Create Date objects for comparison
    const formStart = parse(startTimeStr, 'HH:mm', parseISO(dateStr));
    const formEnd = parse(endTimeStr, 'HH:mm', parseISO(dateStr));

    // RULE 1: Check for overlaps with existing timesheet entries
    const existingEntries = dayData.timesheet?.entries || [];
    for (const entry of existingEntries) {
      const entryStart = parse(entry.start_time, 'HH:mm', parseISO(dateStr));
      const entryEnd = parse(entry.end_time, 'HH:mm', parseISO(dateStr));
      // Overlap condition: (StartA < EndB) and (EndA > StartB)
      if (formStart < entryEnd && formEnd > entryStart) {
        return { isValid: false, message: `Error: Time overlaps with an existing entry for "${entry.project}" (${entry.start_time} - ${entry.end_time}).` };
      }
    }

    // RULE 2: Check if the slot is fully contained within an attendance period
    const attendanceEvents = dayData.attendance?.events || [];
    if (attendanceEvents.length === 0) {
      // Allow entries if status is Leave or Holiday, but not if attendance is just missing.
      if (dayData.status !== 'Leave' && dayData.status !== 'Holiday') {
        return { isValid: false, message: "Error: No attendance recorded for this day. You cannot log time." };
      }
    }

    const sortedEvents = [...attendanceEvents].sort((a, b) =>
      parse(a.time, 'HH:mm', new Date()) - parse(b.time, 'HH:mm', new Date())
    );

    let isContained = false;
    for (let i = 0; i < sortedEvents.length - 1; i += 2) {
      const inEvent = sortedEvents[i];
      const outEvent = sortedEvents[i + 1];

      if (inEvent?.type === 'IN' && outEvent?.type === 'OUT') {
        const availableStart = parse(inEvent.time, 'HH:mm', parseISO(dateStr));
        const availableEnd = parse(outEvent.time, 'HH:mm', parseISO(dateStr));

        if (formStart >= availableStart && formEnd <= availableEnd) {
          isContained = true;
          break; // Found a valid container, no need to check further
        }
      }
    }

    // If there were attendance events but the time slot was not contained, it's an error.
    if (attendanceEvents.length > 0 && !isContained) {
      return { isValid: false, message: "Warning: This time entry is outside your recorded IN/OUT attendance. It will be flagged for review and may require correction before salary generation." };
    }

    return { isValid: true, message: "OK" };
  };


  const submitEntry = async (e) => {
    e.preventDefault();
    if (!userId || !form.project_id) {
      alert("Please select a project.");
      return;
    }

    // --- Perform all validation here ---
    const validationResult = isTimeSlotValid(form.start_at, form.end_at, form.date);
    if (!validationResult.isValid) {
      alert(validationResult.message);
    }

    const sISO = format(parse(form.start_at, 'HH:mm', parseISO(form.date)), "yyyy-MM-dd'T'HH:mm:ss.SSS");
    const eISO = format(parse(form.end_at, 'HH:mm', parseISO(form.date)), "yyyy-MM-dd'T'HH:mm:ss.SSS");

    if (new Date(eISO) <= new Date(sISO)) {
      alert("End time must be after start time.");
      return;
    }

    try {
      await api.post(`${API}/time-entries`, {
        user_id: userId,
        project_id: form.project_id,
        date: form.date,
        task_id: form.task_id,
        start_at: sISO,
        end_at: eISO,
        break_minutes: Number(form.break_minutes || 0),
        billable: form.billable,
        location: form.location,
        notes: form.notes,
      });
      // await fetchAllData();
      await fetchMonthlyData();
    } catch (ex) {
      alert(ex.response?.data?.detail || "Failed to add entry.");
    }
  };

  const doCheck = async (type) => {
    if (!userId) return;
    const url = type === "IN" ? `${API}/attendance/check-in` : `${API}/attendance/check-out`;
    try {
      await api.post(url, { user_id: userId, method: "MANUAL" });
      // await fetchAllData();
      await fetchDailyData();
      await fetchMonthlyData();
    } catch (ex) {
      alert(ex.response?.data?.detail || "Check-in/out failed.");
    }
  };

  const handleCalendarSelect = ({ start, end, date }) => {
    console.log(start, end, date, "start, end, date")
    setForm(prevForm => ({ ...prevForm, date, start_at: start, end_at: end }));
    document.getElementById('quick-time-form')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const lastAtt = attendance.length ? attendance[attendance.length - 1] : null;
  const canCheckIn = !lastAtt || lastAtt.type === "OUT";
  const canCheckOut = lastAtt && lastAtt.type === "IN";

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>My Day</h1>
          <p>Quick check-in/out, time entry and reconciliation • IST • INR</p>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <AttendanceComponent
            attendance={attendance} rec={rec} canCheckIn={canCheckIn}
            canCheckOut={canCheckOut} onCheckIn={() => doCheck("IN")} onCheckOut={() => doCheck("OUT")}
          />
        </Card>

        <Card>
          <CardHeader><CardTitle>Quick Time Entry</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={submitEntry} className="grid grid-cols-2 gap-4" id="quick-time-form">
              <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
              <div><Label>Project</Label><Select value={form.project_id} onValueChange={(v) => setForm({
                ...form,
                project_id: v
              })}><SelectTrigger><SelectValue placeholder="Select project"/></SelectTrigger><SelectContent
                  className="max-h-72">{projects.map((p) => (
                  <SelectItem key={p._id} value={p._id}>{p.name}-({p.current_stage_idx})</SelectItem>))}</SelectContent></Select>
              </div>
              <div>
                <Label>Task(optional)</Label>
                <Select value={form.task_id} onValueChange={(v) => setForm({ ...form, task_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Select task" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {tasks.map((t, index) => (
                      <SelectItem key={t.id || index} value={t.id || `${t.project_id}-${index}`}>
                        {t.title || "Untitled Task"}
                      </SelectItem>
                    ))}

                  </SelectContent>

                </Select>
              </div>


              <div><Label>Start</Label><Input value={form.start_at} type="time" onChange={(e) => setForm({ ...form, start_at: e.target.value })} placeholder="09:30" /></div>
              <div><Label>End</Label><Input value={form.end_at} type="time" onChange={(e) => setForm({ ...form, end_at: e.target.value })} placeholder="10:30" /></div>
              <div><Label>Break (min)</Label><Input type="number" value={form.break_minutes} onChange={(e) => setForm({ ...form, break_minutes: Number(e.target.value || 0) })} /></div>
              <div><Label>Location</Label>
                <Select value={form.location} onValueChange={(v) => setForm({ ...form, location: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="office">Office</SelectItem><SelectItem value="site">Site</SelectItem><SelectItem value="remote">Remote</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optional" /></div>
              <div className="col-span-2"><Button type="submit" className="w-full">Add Entry</Button></div>
            </form>
          </CardContent>
        </Card>

        {loadingCalendar && <Card className="lg:col-span-2 p-6 text-center">Loading Calendar Data...</Card>}
        {calendarError && <Card className="lg:col-span-2 p-6 text-center text-red-600">{calendarError}</Card>}
        {!loadingCalendar && !calendarError && monthlyTimeData && (
          <QuickTimeCalendar
            timeData={monthlyTimeData}
            onSelectTimeSlot={handleCalendarSelect}
            date={form.date}
          />
        )}

        <Card className="lg:col-span-2">
          <CardHeader><CardTitle>Add Voucher</CardTitle></CardHeader>
          <CardContent><VoucherForm defaultDate={form.date} defaultProjectId={form.project_id} userId={userId} projects={projects} onAdded={fetchUserVouchers} /></CardContent>
        </Card>





            <Card className="lg:col-span-2 mt-6 rounded-xl border border-gray-200 shadow-sm">
              <CardHeader className="relative border-b bg-gray-50/60 px-6 py-4 rounded-t-xl">
                <CardTitle className="text-base font-semibold text-gray-800 text-center w-full">
                  Voucher Details
                </CardTitle>

                <div className="absolute right-6 top-1/2 -translate-y-1/2 max-w-[180px]">
                  <Input
                      id="month-picker"
                      type="month"
                      value={month}
                      onChange={(e) => setMonth(e.target.value)}
                      disabled={loadingCalendar}

                  />
                </div>

              </CardHeader>
              {userVouchers.length > 0 && (
              <CardContent className="p-6">
                <div className="space-y-6">
                  {userVouchers.map((voucher, index) => (
                      <div key={voucher.id} className="border-b pb-6">
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                          <div>
                            <p className="text-xs text-gray-500 flex items-center gap-2">
                              <Layers className="h-4 w-4 text-gray-400" />
                              Project
                            </p>
                            <p className="mt-1 font-medium text-gray-800">{voucher.project_name}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 flex items-center gap-2">
                              <User2 className="h-4 w-4 text-gray-400" />
                              User
                            </p>
                            <p className="mt-1 font-medium text-gray-800">{voucher?.user_id?.name}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 flex items-center gap-2">
                              <Calendar className="h-4 w-4 text-gray-400" />
                              Date
                            </p>
                            <p className="mt-1 font-medium text-gray-800">{voucher.date}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 flex items-center gap-2">
                              <FileText className="h-4 w-4 text-gray-400" />
                              Type
                            </p>
                            <p className="mt-1 font-medium text-gray-800">{voucher.type}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 flex items-center gap-2">
                              <CircleDollarSign className="h-4 w-4 text-gray-400" />
                              Amount
                            </p>
                            <p className="mt-1 font-semibold text-green-600">₹{voucher.amount}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 flex items-center gap-2">Status</p>
                            <span
                                className={`mt-1 inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-full
                ${voucher.status === "APPROVED"
                                    ? "bg-green-100 text-green-700"
                                    : voucher.status === "PENDING"
                                        ? "bg-yellow-100 text-yellow-700"
                                        : "bg-red-100 text-red-700"
                                }`}
                            >
              {voucher.status === "APPROVED" && <CheckCircle2 className="h-3.5 w-3.5" />}
                              {voucher.status === "PENDING" && <Hourglass className="h-3.5 w-3.5" />}
                              {voucher.status === "REJECTED" && <XCircle className="h-3.5 w-3.5" />}
                              {voucher.status}
            </span>
                          </div>
                        </div>

                        {voucher.description && (
                            <div className="mt-4">
                              <p className="text-xs text-gray-500 flex items-center gap-2">Description</p>
                              <div className="rounded-lg border bg-gray-50 px-4 py-3 text-sm text-gray-700">
                                {voucher.description}
                              </div>
                            </div>
                        )}

                        {user?.role === "ADMIN" && voucher.status === "PENDING" && (
                            <div className="flex gap-2 justify-end mt-4">
                              <Button
                                  variant="outline"
                                  size="sm"
                                  className="bg-green-50 text-green-700 border-green-200"
                                  onClick={() => handleVoucher(voucher.id, "APPROVED")}
                              >
                                Approve
                              </Button>
                              <Button
                                  variant="outline"
                                  size="sm"
                                  className="bg-red-50 text-red-700 border-red-200"
                                  onClick={() => handleVoucher(voucher.id, "REJECTED")}
                              >
                                Deny
                              </Button>
                            </div>
                        )}

                        <div className="text-xs text-gray-400 text-right mt-2">
                          {index + 1} of {userVouchers.length}
                        </div>
                      </div>
                  ))}
                </div>
              </CardContent>
              )}
            </Card>





      </div>
    </div>
  );
}

export default MyDayPage;